import { useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { X, TrendingUp, TrendingDown, Minus, MapPin, Users, Briefcase, Layers, Loader2 } from "lucide-react";
import { apiGet } from "../lib/api";
import { useTrend } from "../lib/trend";

const RANGE_PRESETS = [
  { id: "7d", label: "Last 7d" },
  { id: "30d", label: "Last 30d" },
  { id: "90d", label: "Last 90d" },
  { id: "qtd", label: "QTD" },
  { id: "ytd", label: "YTD" },
  { id: "custom", label: "Custom" },
];

const BREAKDOWN_TABS = [
  { id: "location",      label: "Location",       icon: MapPin },
  { id: "department",    label: "Department",     icon: Users },
  { id: "business_unit", label: "Business Unit",  icon: Briefcase },
  { id: "asset_class",   label: "Asset Class",    icon: Layers },
];

// -- formatters -------------------------------------------------------------
function fmtVal(v, unit) {
  if (v == null || Number.isNaN(v)) return "—";
  if (unit === "percent") return `${Number(v).toFixed(1)}%`;
  if (unit === "duration") {
    // Small values (< 60) shown with one decimal, otherwise integer
    return Math.abs(v) < 60 ? Number(v).toFixed(1) : String(Math.round(v));
  }
  if (unit === "currency") {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
    return `$${Number(v).toFixed(0)}`;
  }
  // count
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 10_000) return `${(v / 1_000).toFixed(1)}K`;
  // Integers for counts >= 1, else 1 decimal
  return Math.abs(v) >= 1 ? String(Math.round(v)) : Number(v).toFixed(1);
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
}

function fmtDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

// -- sub components ---------------------------------------------------------
function StatTile({ label, value, hint, tone = "var(--cv-charcoal)", testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl px-4 py-3"
      style={{ background: "#fff", border: "1px solid var(--cv-line)" }}
    >
      <div className="text-[10.5px] small-caps font-bold tracking-[0.18em]" style={{ color: "var(--cv-dark-grey)" }}>
        {label}
      </div>
      <div className="font-serif-display leading-none mt-1.5" style={{ fontSize: 26, color: tone }}>
        {value}
      </div>
      {hint && (
        <div className="text-[11.5px] mt-1" style={{ color: "var(--cv-charcoal-soft)" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function DeltaChip({ delta_pct }) {
  if (delta_pct == null) return null;
  const zero = Math.abs(delta_pct) < 0.05;
  const up = delta_pct > 0;
  const Icon = zero ? Minus : up ? TrendingUp : TrendingDown;
  const tone = zero
    ? { bg: "rgba(74,90,106,0.10)", fg: "var(--cv-slate)" }
    : up
    ? { bg: "rgba(62,124,79,0.14)", fg: "var(--cv-green)" }
    : { bg: "rgba(165,48,15,0.14)", fg: "var(--cv-crimson)" };
  const sign = up ? "+" : "";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold tabular-nums"
      style={{ background: tone.bg, color: tone.fg }}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2.6} />
      {sign}{delta_pct}%
    </span>
  );
}

function BreakdownBar({ row, max, unit }) {
  // For percent metrics, bar visualizes the value against the 0..100 scale.
  // Otherwise it visualizes share of the top row.
  const scale = unit === "percent" ? 100 : max;
  const pct = scale > 0 ? (row.value / scale) * 100 : 0;
  // For percent: share_pct is "delta vs overall avg", show as signed pp.
  const rightLabel =
    unit === "percent"
      ? `${row.share_pct > 0 ? "+" : ""}${row.share_pct} pp`
      : `${row.share_pct}%`;
  return (
    <div className="grid grid-cols-[1fr_auto_72px] items-center gap-3 py-2">
      <div className="min-w-0">
        <div className="text-[13px] font-bold truncate" style={{ color: "var(--cv-charcoal)" }}>
          {row.label}
        </div>
        <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--cv-line)" }}>
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background:
                "linear-gradient(90deg, var(--cv-crimson) 0%, var(--cv-orange) 60%, var(--cv-gold) 100%)",
            }}
          />
        </div>
      </div>
      <div className="text-right font-serif-display tabular-nums text-[15px]" style={{ color: "var(--cv-charcoal)" }}>
        {fmtVal(row.value, unit)}
      </div>
      <div className="text-right text-[11.5px] tabular-nums" style={{ color: "var(--cv-dark-grey)" }}>
        {rightLabel}
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, unit }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div
      className="rounded-lg px-3 py-2 text-[12.5px]"
      style={{
        background: "#fff",
        border: "1px solid var(--cv-line)",
        boxShadow: "0 10px 24px rgba(50,50,50,0.12)",
      }}
    >
      <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{fmtDate(p.date)}</div>
      <div className="mt-0.5 font-serif-display text-[16px]" style={{ color: "var(--cv-crimson)" }}>
        {fmtVal(p.value, unit)}
      </div>
    </div>
  );
}

// -- main -------------------------------------------------------------------
export default function TrendAnalyzer() {
  const { ctx, closeTrend } = useTrend();
  const isOpen = !!ctx?.open;
  const widgetId = ctx?.widgetId;
  const label = ctx?.label || widgetId;
  const unit = ctx?.unit || "count";
  const module = ctx?.module || "";
  const current = ctx?.current;

  const [preset, setPreset] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [breakdownTab, setBreakdownTab] = useState("location");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset when a new widget opens
  useEffect(() => {
    if (!isOpen) return;
    setPreset("30d");
    setCustomStart("");
    setCustomEnd("");
    setBreakdownTab("location");
    setData(null);
  }, [isOpen, widgetId]);

  // Fetch on any control change
  useEffect(() => {
    if (!isOpen || !widgetId) return;
    if (preset === "custom" && (!customStart || !customEnd)) return;
    let cancelled = false;
    setLoading(true);
    apiGet(`/trend/${encodeURIComponent(widgetId)}`, {
      label,
      module,
      unit,
      current: Number.isFinite(current) ? current : 0,
      preset,
      start: preset === "custom" ? customStart : undefined,
      end: preset === "custom" ? customEnd : undefined,
    })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, widgetId, label, module, unit, current, preset, customStart, customEnd]);

  const series = data?.series || [];
  const stats = data?.stats || {};
  const breakdowns = data?.breakdowns || {};
  const activeRows = breakdowns[breakdownTab] || [];
  const maxRow = useMemo(
    () => activeRows.reduce((m, r) => Math.max(m, r.value), 0),
    [activeRows]
  );

  const trendTone = (stats.delta_pct ?? 0) >= 0 ? "var(--cv-green)" : "var(--cv-crimson)";

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(o) => { if (!o) closeTrend(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ background: "rgba(28,20,14,0.55)", backdropFilter: "blur(2px)" }}
        />
        <DialogPrimitive.Content
          data-testid="trend-analyzer"
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{
            width: "min(1280px, 96vw)",
            height: "min(88vh, 900px)",
            background: "#ffffff",
            border: "1px solid var(--cv-line)",
            borderRadius: 16,
            boxShadow: "0 40px 90px rgba(50,50,50,0.28)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Accessible dialog title/description (visually hidden, screen-reader only) */}
          <DialogPrimitive.Title asChild>
            <span className="sr-only">{`Trend Analyzer · ${label}`}</span>
          </DialogPrimitive.Title>
          <DialogPrimitive.Description asChild>
            <span className="sr-only">
              {`Time-series and location, department, business unit and asset class breakdowns for ${label}.`}
            </span>
          </DialogPrimitive.Description>

          {/* Top accent bar */}
          <div
            aria-hidden
            style={{
              height: 3,
              flexShrink: 0,
              background:
                "linear-gradient(90deg, var(--cv-crimson) 0%, var(--cv-orange) 45%, var(--cv-gold) 100%)",
            }}
          />

          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-7 pt-5 pb-4"
               style={{ borderBottom: "1px solid var(--cv-line)" }}>
            <div className="min-w-0">
              <div className="text-[10.5px] small-caps font-bold tracking-[0.24em]" style={{ color: "var(--cv-orange)" }}>
                {module ? `${module.toUpperCase()} · Trend Analyzer` : "Trend Analyzer"}
              </div>
              <div className="font-serif-display leading-tight mt-1 flex items-center gap-3 flex-wrap"
                   style={{ fontSize: 26, color: "var(--cv-charcoal)" }}>
                <span data-testid="trend-label">{label}</span>
                <DeltaChip delta_pct={stats.delta_pct} />
              </div>
              {data?.range && (
                <div className="text-[12.5px] mt-1" style={{ color: "var(--cv-charcoal-soft)" }}>
                  {fmtDate(data.range.start)} → {fmtDate(data.range.end)} · {data.range.granularity}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="trend-close"
              onClick={closeTrend}
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "var(--cv-sand)", border: "1px solid var(--cv-line)", color: "var(--cv-dark-grey)" }}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Controls row */}
          <div className="flex flex-wrap items-center gap-3 px-7 py-3"
               style={{ borderBottom: "1px solid var(--cv-line)", background: "var(--cv-sand)" }}>
            <div className="flex items-center gap-1 flex-wrap" data-testid="trend-range-tabs">
              {RANGE_PRESETS.map((p) => {
                const active = preset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`trend-range-${p.id}`}
                    onClick={() => setPreset(p.id)}
                    className="px-3 py-1.5 rounded-full text-[12px] font-bold small-caps tracking-[0.08em] transition-colors"
                    style={{
                      background: active ? "#fff" : "transparent",
                      border: active ? "1px solid var(--cv-orange)" : "1px solid transparent",
                      color: active ? "var(--cv-crimson)" : "var(--cv-dark-grey)",
                      boxShadow: active ? "0 2px 6px rgba(165,48,15,0.10)" : "none",
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 ml-1" data-testid="trend-custom-range">
                <label className="text-[11px] small-caps font-bold tracking-[0.12em]" style={{ color: "var(--cv-dark-grey)" }}>
                  From
                </label>
                <input
                  type="date"
                  data-testid="trend-date-start"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="h-8 px-2 rounded-md text-[13px]"
                  style={{ border: "1px solid var(--cv-line)", background: "#fff", color: "var(--cv-charcoal)" }}
                />
                <label className="text-[11px] small-caps font-bold tracking-[0.12em] ml-1" style={{ color: "var(--cv-dark-grey)" }}>
                  To
                </label>
                <input
                  type="date"
                  data-testid="trend-date-end"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="h-8 px-2 rounded-md text-[13px]"
                  style={{ border: "1px solid var(--cv-line)", background: "#fff", color: "var(--cv-charcoal)" }}
                />
              </div>
            )}
            {loading && (
              <div className="ml-auto flex items-center gap-2 text-[12px]" style={{ color: "var(--cv-dark-grey)" }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> loading
              </div>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-7 py-5">
            {/* Stat row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="trend-stats">
              <StatTile label="Current" value={fmtVal(stats.current, unit)} testid="trend-stat-current" tone="var(--cv-crimson)"
                        hint={data?.range ? `as at ${fmtDateShort(data.range.end)}` : ""} />
              <StatTile label="Period Δ" value={<span style={{ color: trendTone }}>{stats.delta_pct != null ? `${stats.delta_pct > 0 ? "+" : ""}${stats.delta_pct}%` : "—"}</span>}
                        hint="first vs last point" testid="trend-stat-delta" />
              <StatTile label="Min · Max" value={`${fmtVal(stats.min, unit)} · ${fmtVal(stats.max, unit)}`} hint="over selected range" testid="trend-stat-range" />
              <StatTile label="Average" value={fmtVal(stats.avg, unit)} hint="mean over selected range" testid="trend-stat-avg" />
            </div>

            {/* Chart panel */}
            <div className="rounded-xl p-4 mb-5"
                 style={{ background: "#fff", border: "1px solid var(--cv-line)" }}
                 data-testid="trend-chart-panel">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[13.5px] font-bold small-caps tracking-[0.06em]" style={{ color: "var(--cv-charcoal)" }}>
                    Trend over time
                  </div>
                  <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>
                    X-axis · date · {data?.range?.granularity || "daily"} · Y-axis · {unit === "percent" ? "% value" : unit === "currency" ? "amount" : unit === "duration" ? "duration" : "count"}
                  </div>
                </div>
                <div className="text-[11px] small-caps font-bold tracking-[0.14em]" style={{ color: "var(--cv-dark-grey)" }}>
                  {series.length} data points
                </div>
              </div>
              <div style={{ width: "100%", height: 300 }} data-testid="trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series} margin={{ top: 10, right: 20, bottom: 8, left: 8 }}>
                    <defs>
                      <linearGradient id="cvTrendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"  stopColor="#A5300F" stopOpacity={0.28} />
                        <stop offset="60%" stopColor="#D55816" stopOpacity={0.12} />
                        <stop offset="100%" stopColor="#E5C243" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--cv-line)" strokeDasharray="3 4" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={fmtDateShort}
                      tick={{ fill: "var(--cv-dark-grey)", fontSize: 11 }}
                      stroke="var(--cv-line)"
                      minTickGap={30}
                    />
                    <YAxis
                      tick={{ fill: "var(--cv-dark-grey)", fontSize: 11 }}
                      stroke="var(--cv-line)"
                      tickFormatter={(v) => fmtVal(v, unit)}
                      width={64}
                    />
                    {stats.avg != null && (
                      <ReferenceLine
                        y={stats.avg}
                        stroke="var(--cv-brown)"
                        strokeDasharray="5 4"
                        strokeOpacity={0.55}
                        label={{ value: "avg", position: "right", fill: "var(--cv-brown)", fontSize: 10 }}
                      />
                    )}
                    <Tooltip content={<ChartTooltip unit={unit} />} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="var(--cv-crimson)"
                      strokeWidth={2.2}
                      fill="url(#cvTrendFill)"
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Breakdown */}
            <div className="rounded-xl p-4"
                 style={{ background: "#fff", border: "1px solid var(--cv-line)" }}
                 data-testid="trend-breakdown-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="text-[13.5px] font-bold small-caps tracking-[0.06em]" style={{ color: "var(--cv-charcoal)" }}>
                    Breakdown
                  </div>
                  <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>
                    Share of current value across dimensions
                  </div>
                </div>
                <div className="flex flex-wrap gap-1" data-testid="trend-breakdown-tabs">
                  {BREAKDOWN_TABS.map((t) => {
                    const Icon = t.icon;
                    const active = breakdownTab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`trend-tab-${t.id}`}
                        onClick={() => setBreakdownTab(t.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold small-caps tracking-[0.08em] transition-colors"
                        style={{
                          background: active ? "#fff" : "var(--cv-sand)",
                          border: active ? "1px solid var(--cv-crimson)" : "1px solid var(--cv-line)",
                          color: active ? "var(--cv-crimson)" : "var(--cv-dark-grey)",
                        }}
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {activeRows.length === 0 ? (
                <div className="text-[13px] py-6 text-center" style={{ color: "var(--cv-dark-grey)" }}>
                  {loading ? "Loading breakdown…" : "No breakdown data."}
                </div>
              ) : (
                <div data-testid="trend-breakdown-list">
                  {activeRows.map((r) => (
                    <BreakdownBar key={r.label} row={r} max={maxRow} unit={unit} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
